import type { LocaleString } from '@dreamstack/i18n'
import {
  useFormatToCurrencyCutoff,
  useFormatToSoftDecimal,
  useLocale,
} from '@dreamstack/i18n'
import type { BlockRibbon } from '@dreamstack/payload/src/payload-types'
import { useOnScreen } from '@dreamstack/util'
import { clsxMerge, cn } from '@dreamstack/util/src/cn'
import { motion, useAnimation } from 'framer-motion'
import filter from 'lodash/filter'
import first from 'lodash/first'
import map from 'lodash/map'
import shuffle from 'lodash/shuffle'
import type { FunctionComponent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CountUp } from 'use-count-up'

export const BUBBLES_COUNTER_DEFAULT_DURATION = 1.5

export const useAnimateControls = ({
  onScreen,
  duration,
}: {
  onScreen: boolean
  duration: number
}) => {
  const controls = useAnimation()

  const [animationPlayed, setAnimationPlayed] = useState(false)

  useEffect(() => {
    if (onScreen && !animationPlayed) {
      controls.start({
        scale: [1, 1.2, 1],
        transition: {
          delay: duration - 0.125,
          duration: 0.25,
        },
      })
      setAnimationPlayed(true)
    }
  }, [animationPlayed, controls, duration, onScreen])

  return controls
}

type BubbleContainerProps = {
  backgroundColor: string
  index: number
  length: number
  children: React.ReactNode
}

const BubbleContainer: React.FC<BubbleContainerProps> = ({
  backgroundColor,
  index,
  length,
  children,
}) => {
  const first = index === 0
  const uneven = length % 2 === 1
  const isLastAndUneven = uneven && index + 1 === length
  const isRightColumn = index % 2 === 1
  return (
    <motion.div
      style={{ backgroundColor }}
      className={cn(
        // base
        'p-px-16 flex flex-col justify-center items-center rounded-full h-[150px] w-[150px] sm:h-[250px] sm:w-[250px] md:p-px-24 md:h-[200px] md:w-[200px] ml:px-px-20 ml:h-[300px] ml:w-[300px] ml:p-px-48 lg:px-px-40 lg:h-[400px] lg:w-[400px]',
        // column offset handling
        isRightColumn ? '-ml-px-16 sm:-ml-px-20' : 'sm:-ml-0',
        !first &&
          '-mt-px-16 md:-ml-px-20 md:mt-0 ml:-ml-px-40 ml:mt-0 lg:-ml-px-40 lg:mt-0',
        index === 1 && 'mt-0',
        isLastAndUneven &&
          'translate-x-1/2 -ml-px-8 -mt-px-24 sm:-mt-px-40 sm:-ml-px-8 md:translate-x-0 md:mt-0 md:-ml-px-20'
      )}
    >
      {children}
    </motion.div>
  )
}

type BubbleBigFontProps = {
  teal?: boolean
  animateControls: any
  children: React.ReactNode
}
const BubbleBigFont: React.FC<BubbleBigFontProps> = ({
  teal,
  animateControls,
  children,
}) => (
  <motion.p
    animate={animateControls}
    className={clsxMerge(
      teal ? 'text-accentroAqua-full' : 'text-accentroWhite-full',
      'text-px-32 sm:text-px-48 tabular-nums mt-px-12 md:text-px-40 ml:text-[60px] ml:mt-px-16 lg:text-[90px] leading-none font-semibold'
    )}
  >
    {children}
  </motion.p>
)

type BubbleSmallFontProps = { fullGray?: boolean; subheading: string }
const BubbleSmallFont: React.FC<BubbleSmallFontProps> = ({
  fullGray,
  subheading,
}) => (
  <p
    className={clsxMerge(
      fullGray ? 'text-accentroGray-full' : 'text-accentroWhite-full',
      'min-h-[50px] text-px-13 leading-px-18 sm:text-px-16 sm:leading-px-24 md:text-px-13 md:leading-px-18 ml:text-px-16 ml:leading-px-24 lg:text-px-24 lg:leading-px-36 lg:min-h-[72px] text-center mt-px-8'
    )}
    // original used dangerouslySetInnerHTML
    dangerouslySetInnerHTML={{ __html: subheading }}
  />
)

const getIntOrFloatRegexForLocale = (local: LocaleString) => {
  const regex =
    local === 'de' ? /([0-9]+\,[0-9]+)|([0-9]+)/g : /([0-9]+\.[0-9]+)|([0-9]+)/g
  return regex
}

const getSurroundingString = (regex: RegExp, text?: string) => {
  return text?.replace(regex, '$')
}

const getCountFromString = (regex: RegExp, text?: string) => {
  const matches = text?.match(regex)
  return first(matches)
}

export const parseHeadingString = (locale: LocaleString, text?: string) => {
  const regex = getIntOrFloatRegexForLocale(locale)
  const surroundingString = getSurroundingString(regex, text)
  const countRegexResult = getCountFromString(regex, text)
  const count = countRegexResult
    ? parseFloat(countRegexResult?.replace(',', '.'))
    : null
  return {
    surroundingString,
    count,
  }
}

export const bubbleConfigs = [
  {
    bigFontTeal: true,
    smallFontGray: true,
    backgroundColor: 'rgb(237, 241, 242)',
  },
  {
    bigFontTeal: true,
    smallFontGray: true,
    backgroundColor: 'rgb(211, 223, 227)',
  },
  {
    bigFontTeal: false,
    smallFontGray: false,
    backgroundColor: 'rgb(80, 128, 144)',
  },
  {
    bigFontTeal: false,
    smallFontGray: false,
    backgroundColor: 'rgba(43, 45, 54, 1.0)',
  },
]

const Bubble: FunctionComponent<
  React.PropsWithChildren<{
    bigFontTeal?: boolean
    smallFontGray?: boolean
    count?: number | null
    subheading: string
    backgroundColor: string
    surroundingString?: string | null
    index: number
    onScreen: boolean
    length: number
    animationDuration: number
  }>
> = ({
  bigFontTeal,
  smallFontGray,
  count,
  subheading,
  backgroundColor,
  index,
  surroundingString,
  onScreen,
  length,
  animationDuration = BUBBLES_COUNTER_DEFAULT_DURATION,
}) => {
  const decimalFormat = useFormatToSoftDecimal({
    maxFractionDigits: 1,
    minFractionDigits: 1,
  })
  const cutoffFormat = useFormatToCurrencyCutoff()
  const format =
    typeof count === 'number' && count % 1 === 0 ? cutoffFormat : decimalFormat

  // Keep seperator while splitting, so we can determine pre and post based on array position of the seperator.
  const splitted = surroundingString?.split(/(\$)/g)
  const indexOfCount = splitted?.indexOf('$')
  const preText = indexOfCount ? splitted?.[indexOfCount - 1] : null
  const postText = indexOfCount ? splitted?.[indexOfCount + 1] : null

  const duration = animationDuration // random(1.2, 1.8, true)

  const controls = useAnimateControls({ duration, onScreen })

  return (
    <BubbleContainer
      backgroundColor={backgroundColor}
      index={index}
      length={length}
    >
      <BubbleBigFont teal={bigFontTeal} animateControls={controls}>
        {preText}
        {!!count && !isNaN(count) && (
          <CountUp
            duration={duration}
            end={count}
            isCounting={onScreen}
            formatter={format}
          />
        )}
        {postText}
      </BubbleBigFont>
      <BubbleSmallFont fullGray={smallFontGray} subheading={subheading} />
    </BubbleContainer>
  )
}

export const BubblesRibbon: FunctionComponent<
  React.PropsWithChildren<{
    block: BlockRibbon
  }>
> = ({ block }) => {
  const declaredBubbles = useMemo(() => {
    return filter(
      [
        { heading: block.heading1, subheading: block.subheading1 },
        { heading: block.heading2, subheading: block.subheading2 },
        { heading: block.heading3, subheading: block.subheading3 },
        { heading: block.heading4, subheading: block.subheading4 },
      ],
      (bubble) => !!bubble.heading
    )
  }, [
    block.heading1,
    block.heading2,
    block.heading3,
    block.heading4,
    block.subheading1,
    block.subheading2,
    block.subheading3,
    block.subheading4,
  ])

  const locale = useLocale()
  const ref = useRef<HTMLDivElement | null>(null)
  const onScreen = useOnScreen(ref)
  const initialDuration = 1.2
  const durationPresets = shuffle(
    map(declaredBubbles, (_, index) => {
      return initialDuration + index * 0.2
    })
  )

  return (
    <div className="flex justify-center" ref={ref}>
      <div className="grid grid-cols-2 gap-0 items-center justify-center sm:w-[500px] md:flex md:flex-row md:w-auto">
        {map(declaredBubbles, (bubble, index) => {
          const drawDuration = durationPresets[index]
          const config = {
            ...bubbleConfigs[index],
            ...parseHeadingString(locale, bubble.heading ?? undefined),
          }

          return (
            <Bubble
              onScreen={onScreen}
              key={index}
              {...config}
              subheading={bubble.subheading ?? ''}
              index={index}
              length={declaredBubbles.length}
              animationDuration={drawDuration}
            />
          )
        })}
      </div>
    </div>
  )
}
